import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, ShieldIcon, BellIcon, HelpCircleIcon, InfoIcon, LogOutIcon, XIcon, ChevronRightIcon, PaletteIcon } from 'lucide-react';
import { useTranslation } from '../utils/translations';
import { useTheme } from '../utils/theme';
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout?: () => void;
  onNavigateToRaast?: () => void;
  onNavigateToProfile?: () => void;
}
const RAAST_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1ZmoJtWAAdB82VYTszuaqM/pasted-image.svg';
export function Sidebar({
  isOpen,
  onClose,
  onLogout,
  onNavigateToRaast,
  onNavigateToProfile
}: SidebarProps) {
  const {
    t,
    language,
    setLanguage,
    isRtl
  } = useTranslation();
  const {
    theme,
    setTheme,
    isDark
  } = useTheme();
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'platinum'>(theme);
  const handleSaveTheme = () => {
    setTheme(selectedTheme);
    setShowThemePanel(false);
  };
  type MenuItem = {
    icon?: any;
    imageSrc?: string;
    label: string;
    color: string;
    onClick?: () => void;
    isLogout?: boolean;
  };
  const menuItems: MenuItem[] = [{
    icon: UserIcon,
    label: t('accountSettings'),
    color: '#661806'
  }, {
    icon: ShieldIcon,
    label: t('security'),
    color: '#10B981'
  }, {
    imageSrc: RAAST_LOGO_URL,
    label: 'RAAST Transfer',
    color: '#16A34A',
    onClick: () => {
      onClose();
      onNavigateToRaast?.();
    }
  }, {
    icon: BellIcon,
    label: t('notifications'),
    color: '#F37024'
  }, {
    icon: PaletteIcon,
    label: 'Change Theme',
    color: '#8B5CF6',
    onClick: () => setShowThemePanel(true)
  }, {
    icon: HelpCircleIcon,
    label: t('helpSupport'),
    color: '#3B82F6'
  }, {
    icon: InfoIcon,
    label: t('about'),
    color: '#8B5CF6'
  }];
  return <AnimatePresence data-id="element-3497" data-mp="2m0inl3ix8bqq">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/50 z-[100] backdrop-blur-sm" data-id="element-3498" data-mp="2h893430ks3vu" />

          {/* Sidebar */}
          <motion.div initial={{
        x: isRtl ? '100%' : '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: isRtl ? '100%' : '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className={`absolute top-0 bottom-0 ${isRtl ? 'right-0' : 'left-0'} w-[280px] ${isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF7F3]'} z-[101] shadow-2xl flex flex-col overflow-hidden`} data-id="element-3499" data-mp="23b2h7zkk1p2e">
            {/* Header / Profile Section */}
            <div className="bg-gradient-to-br from-[#661806] to-[#D95A1A] p-6 pt-12 pb-8 relative" data-id="element-3500" data-mp="1fbbh0mqgj58h">
              <button onClick={onClose} className={`absolute top-6 ${isRtl ? 'left-6' : 'right-6'} text-white/80 hover:text-white`} data-id="element-3501" data-mp="1o5yg715j2lca">
                <XIcon className="w-6 h-6" strokeWidth={2} data-id="element-3502" data-mp="rhs6hztr12r5" />
              </button>
              <div className="flex items-center gap-4" data-id="element-3503" data-mp="wfbs5kss0ht4">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-[#F37024] text-xl font-bold shadow-md" data-id="element-3504" data-mp="10fvq2ql6bpgb">
                  K
                </div>
                <div data-id="element-3505" data-mp="2bvss9gi0w5vh">
                  <h3 className="text-lg font-bold text-white" data-id="element-3506" data-mp="2bwbxbwmkqzs3">Kashish</h3>
                  <p className="text-sm text-white/80" dir="ltr" data-id="element-3507" data-mp="2gudh6jrtr34m">
                    +92 302 2746326
                  </p>
                  <button onClick={() => {
                onClose();
                onNavigateToProfile?.();
              }} className="text-[10px] font-light text-white/90 hover:text-white mt-0.5 underline underline-offset-2" data-id="element-3508" data-mp="2fcomjupw6ub3">
                    My Profile
                  </button>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-4" data-id="element-3509" data-mp="2ya9dd7c7w2yc">
              <div className="px-4 space-y-1" data-id="element-3510" data-mp="2tc7pm93ujfgj">
                {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeIndex === index;
              return <button key={index} onClick={() => {
                setActiveIndex(index);
                item.onClick?.();
              }} className={`relative w-full pl-5 pr-4 py-3.5 flex items-center gap-4 ${isActive ? isDark ? 'bg-[#2D1810]' : 'bg-[#FFF1E8]' : isDark ? 'hover:bg-[#2D1810]' : 'hover:bg-white'} rounded-xl transition-colors group`} data-id="element-3511" data-mp="333u9etu92muw" data-mpi={index}>
                      {/* Orange left accent bar */}
                      <span aria-hidden className={`absolute top-2 bottom-2 ${isRtl ? 'right-1' : 'left-1'} w-[3px] rounded-full transition-opacity`} style={{
                  background: '#F37024',
                  opacity: isActive ? 1 : 0
                }} data-id="element-3512" data-mp="3qqfs4hs4h6oy" />
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 overflow-hidden" style={{
                  backgroundColor: item.imageSrc ? '#FFFFFF' : `${item.color}15`,
                  border: item.imageSrc ? '1px solid rgba(0,0,0,0.06)' : 'none'
                }} data-id="element-3513" data-mp="32ysakqgr37zc">
                        {item.imageSrc ? <img src={item.imageSrc} alt="RAAST" className="w-7 h-7 object-contain" data-id="element-3514" data-mp="qrmyv6nr96um" /> : Icon && <Icon className="w-5 h-5" style={{
                    color: item.color
                  }} strokeWidth={2} data-id="element-3515" data-mp="1igq34mfn6h79" />}
                      </div>
                      <span className="flex-1 text-left text-[15px] font-semibold" style={{
                  color: isActive ? '#F37024' : isDark ? '#FFFFFF' : '#333333'
                }} data-id="element-3516" data-mp="3u7bw5npsn9hr">
                        {item.label}
                      </span>
                      <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'} ${isRtl ? 'rotate-180' : ''}`} strokeWidth={2} data-id="element-3517" data-mp="2sgsshsqja0sr" />
                    </button>;
            })}
              </div>

              {/* Logout - pinned at bottom of menu list */}
              <div className={`mt-4 mx-4 pt-3 border-t ${isDark ? 'border-[#2D1810]' : 'border-[#F0E8E3]'}`} data-id="element-3518" data-mp="36fsf6uuhk5ma">
                <button onClick={onLogout} className={`w-full pl-5 pr-4 py-3.5 flex items-center gap-4 rounded-xl transition-colors ${isDark ? 'hover:bg-[#2D1810]' : 'hover:bg-[#FEE2E2]'} group`} data-id="element-3519" data-mp="1c20zbdlkcpie">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{
                backgroundColor: '#EF444415'
              }} data-id="element-3520" data-mp="o6z06uepwwg6">
                    <LogOutIcon className="w-5 h-5" style={{
                  color: '#EF4444'
                }} strokeWidth={2} data-id="element-3521" data-mp="a7zkwdd3htwo" />
                  </div>
                  <span className="flex-1 text-left text-[15px] font-semibold" style={{
                color: '#EF4444'
              }} data-id="element-3522" data-mp="e8jl5psytdn1">
                    {t('logout')}
                  </span>
                </button>
              </div>
            </div>

            {/* Footer Section */}
            <div className={`p-6 ${isDark ? 'bg-[#1A0E08] border-[#2D1810]' : 'bg-white border-[#F0E8E3]'} border-t`} data-id="element-3523" data-mp="1y7l9to2qfgiv">
              {/* Language Toggle */}
              <div className="flex items-center justify-between mb-6" data-id="element-3524" data-mp="1o8vp7njgbhyx">
                <span className={`text-[14px] font-semibold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3525" data-mp="1t772k88ygalw">
                  {t('language')}
                </span>
                <div className={`flex ${isDark ? 'bg-[#2D1810]' : 'bg-[#F5EBE6]'} rounded-full p-1`} data-id="element-3526" data-mp="2ltrt56hlwioe">
                  <button onClick={() => setLanguage('en')} className={`px-4 py-1.5 rounded-full text-[13px] font-bold transition-all ${language === 'en' ? 'bg-[#661806] text-white shadow-sm' : `${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`}`} data-id="element-3527" data-mp="273ss8vyo2t58">
                    EN
                  </button>
                  <button onClick={() => setLanguage('ur')} className={`px-4 py-1.5 rounded-full text-[13px] font-bold font-urdu transition-all ${language === 'ur' ? 'bg-[#661806] text-white shadow-sm' : `${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`}`} data-id="element-3528" data-mp="3r8p75aphlab8">
                    اردو
                  </button>
                </div>
              </div>

              {/* Version Info */}
              <div className="text-center" data-id="element-3529" data-mp="38fbitoga424l">
                <p className={`text-[12px] font-bold ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3530" data-mp="3d84pwiz7zmju">
                  digiBOP v2.1.0
                </p>
                <p className={`text-[11px] ${isDark ? 'text-[#B8A89E]/70' : 'text-[#818285]/70'} mt-0.5`} data-id="element-3531" data-mp="33ha054mrm4tn">
                  © 2025 Bank of Punjab
                </p>
              </div>
            </div>
          </motion.div>

          {/* Theme Selection Panel */}
          <AnimatePresence data-id="element-3532" data-mp="2aus42qub2h7d">
            {showThemePanel && <>
                <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowThemePanel(false)} className="absolute inset-0 bg-black/40 z-[102]" data-id="element-3533" data-mp="25wql05wvqhvw" />
                <motion.div initial={{
            opacity: 0,
            y: 50,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 50,
            scale: 0.95
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} className={`absolute inset-x-4 bottom-8 z-[103] ${isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF7F3]'} rounded-[28px] p-6 shadow-2xl max-w-sm mx-auto`} data-id="element-3534" data-mp="2ow4jn15k0gsq">
                  <div className="flex items-center justify-between mb-6" data-id="element-3535" data-mp="3ub6blsvsia4l">
                    <h3 className={`text-[20px] font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3536" data-mp="3ud9al2ex7dlg">
                      Change Theme
                    </h3>
                    <button onClick={() => setShowThemePanel(false)} className={`w-8 h-8 rounded-full ${isDark ? 'bg-[#2D1810]' : 'bg-[#F5EBE6]'} flex items-center justify-center`} data-id="element-3537" data-mp="c5tkevnwgb3r">
                      <XIcon className={`w-4 h-4 ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} strokeWidth={2.5} data-id="element-3538" data-mp="2dyhqrdvubenr" />
                    </button>
                  </div>

                  <div className="space-y-3 mb-6" data-id="element-3539" data-mp="102zntxwk356s">
                    {/* Light Theme */}
                    <button onClick={() => setSelectedTheme('light')} className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all ${selectedTheme === 'light' ? `${isDark ? 'bg-[#2D1810]' : 'bg-white'} border-2 border-[#F37024] shadow-sm` : `${isDark ? 'bg-[#1A0E08]' : 'bg-white'} border-2 ${isDark ? 'border-[#3D2518]' : 'border-[#F0E8E3]'}`}`} data-id="element-3540" data-mp="v8bw7q699unz">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFF7F3] to-[#FDEEE5] border-2 border-[#F0E8E3] flex-shrink-0" data-id="element-3541" data-mp="19z2ot0fo8xs5" />
                      <span className={`flex-1 text-left text-[15px] font-semibold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3542" data-mp="14zpyu6ti7alc">
                        Light
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedTheme === 'light' ? 'border-[#F37024]' : 'border-[#818285]/40'}`} data-id="element-3543" data-mp="1hhsfy3xwipwb">
                        {selectedTheme === 'light' && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-3544" data-mp="18ezr07c6yq9n" />}
                      </div>
                    </button>

                    {/* Dark Theme */}
                    <button onClick={() => setSelectedTheme('dark')} className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all ${selectedTheme === 'dark' ? `${isDark ? 'bg-[#2D1810]' : 'bg-white'} border-2 border-[#F37024] shadow-sm` : `${isDark ? 'bg-[#1A0E08]' : 'bg-white'} border-2 ${isDark ? 'border-[#3D2518]' : 'border-[#F0E8E3]'}`}`} data-id="element-3545" data-mp="213x661l3w6y6">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-2 border-[#2d2d44] flex-shrink-0" data-id="element-3546" data-mp="kr898o7vtl0z" />
                      <span className={`flex-1 text-left text-[15px] font-semibold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3547" data-mp="d65tlgsod44g">
                        Dark
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedTheme === 'dark' ? 'border-[#F37024]' : 'border-[#818285]/40'}`} data-id="element-3548" data-mp="h9kkj2i5j4wo">
                        {selectedTheme === 'dark' && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-3549" data-mp="3llzahwqx4hm1" />}
                      </div>
                    </button>

                    {/* Platinum Theme */}
                    <button onClick={() => setSelectedTheme('platinum')} className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all ${selectedTheme === 'platinum' ? `${isDark ? 'bg-[#2D1810]' : 'bg-white'} border-2 border-[#F37024] shadow-sm` : `${isDark ? 'bg-[#1A0E08]' : 'bg-white'} border-2 ${isDark ? 'border-[#3D2518]' : 'border-[#F0E8E3]'}`}`} data-id="element-3550" data-mp="3umnsf2jfn247">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E8E8E8] to-[#B8B8B8] border-2 border-[#D0D0D0] flex-shrink-0" data-id="element-3551" data-mp="1fm7zrwr4ric3" />
                      <span className={`flex-1 text-left text-[15px] font-semibold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3552" data-mp="1knnl7px7zqv0">
                        Platinum
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedTheme === 'platinum' ? 'border-[#F37024]' : 'border-[#818285]/40'}`} data-id="element-3553" data-mp="wvgwncxqko65">
                        {selectedTheme === 'platinum' && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-3554" data-mp="2cch1fnnmh44l" />}
                      </div>
                    </button>
                  </div>

                  <button onClick={handleSaveTheme} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold text-[16px] rounded-[20px] shadow-[0_8px_20px_rgba(217,90,26,0.25)] active:scale-[0.98] transition-all" data-id="element-3555" data-mp="3st699zc8eny7">
                    Save Theme
                  </button>
                </motion.div>
              </>}
          </AnimatePresence>
        </>}
    </AnimatePresence>;
}